/**
 * leadform_ink1 (2026-10-18 · Node 11:48 「Ink & White」) — 관심고객 폼 한 벌의 «문구·요약·전송 묶음» 순수 함수.
 *
 * ⚠️ 화면(LeadForm.tsx)은 'use client' 다. 판정·문구·payload 조립을 여기 두면
 *    서버 페이지(요약 3칸 재료)와 테스트(payload 모양)가 같은 함수를 부른다 — 두 벌이 생기지 않는다.
 * ⚠️ 입력 칸은 이름·연락처·생년월일 3개(허브는 생년월일 없음 — 종전 규칙). «희망 타입» 칸은 화면에서 뺐고
 *    저장 호환을 위해 desiredType 은 「미정」 고정으로 보낸다(leads.desired_type · 시트 열 그대로).
 * ⛔ 「광고 전화 없음」 같은 약속 문구를 만들지 않는다. 확정 전 내용을 확정처럼 쓰지 않는다.
 */
import { leadCopyKind, type LeadCopy } from '@/lib/apt/lead-copy';
import { daysBetween, normDate } from '@/lib/apt/schedule';
import { nameWithoutNumbers, numericTokens } from '@/lib/cl-core/numbers';

/** 희망 타입 저장값 — 화면 칸은 없고 이 값으로 고정 전송한다. */
export const LEAD_DESIRED_TYPE_FIXED = '미정';

export type LeadVariant = 'detail' | 'blog' | 'home' | 'region';

/** 문구 계열. sale = 분양(공고 전 비정비 현장 포함) · redev = 공고 전 정비 · resale = 기축 매물 · existing = 입주 끝난 단지(같은 지역 분양) */
export type LeadInkKind = 'sale' | 'redev' | 'resale' | 'existing' | 'redev_hub' | 'area_hub' | 'home';

export function leadInkKind(input: {
  variant: LeadVariant; lifecycleStage?: string | null; isRedev?: boolean; redevInProgress?: boolean; copy: LeadCopy;
}): LeadInkKind {
  if (input.variant === 'home') return 'home';
  // 허브 두 계열 — lead-copy 가 고른 inquiryType 이 이미 말한다(redev 허브만 '진행상황알림').
  if (input.variant === 'region') return input.copy.inquiryType === '진행상황알림' ? 'redev_hub' : 'area_hub';
  const k = leadCopyKind(input.lifecycleStage, { redevInProgress: input.redevInProgress });
  if (k === 'resale') return 'resale';
  if (k === 'existing') return 'existing';
  // 공고 전: 정비(모르면 정비판 — lead-copy 와 같은 기본값)만 「분양」 말을 쓰지 않는다.
  if (k === 'pre_notice' && input.isRedev !== false) return 'redev';
  return 'sale';
}

export interface LeadInkCopy {
  /** 눈썹 글 — 「분양 알림 · 청약 D-n」 */
  eyebrow: string;
  /** 제목 1줄(단지명) */
  titleName: string;
  /** 제목 2줄(400 회색) */
  titleSub: string;
  /** 아래 안내 1줄 */
  note: string;
  /** 아래 안내 2줄 */
  note2: string;
  /** 바텀시트 제목 */
  sheetTitle: string;
  /** 완료 문장 — 마스킹 번호를 받는다 */
  doneLine: (masked: string) => string;
}

const NOTE2 = '확정 전 내용은 확정으로 안내하지 않습니다';

/** D-n 표기 — 0 은 D-day. 음수·null 은 표기 없음. */
export function ddayText(dday: number | null | undefined): string | null {
  if (dday == null || !Number.isFinite(dday) || dday < 0) return null;
  return dday === 0 ? 'D-day' : `D-${dday}`;
}

export function leadInkCopy(input: {
  kind: LeadInkKind; copy: LeadCopy; siteName?: string; regionName?: string; dday?: number | null;
}): LeadInkCopy {
  const { kind, copy } = input;
  // CL5 — 수치 토큰 든 이름(「10년」 등)은 h1 밖에서 수치를 걷는다(상세 h2·의도 이름과 같은 규칙).
  const siteRaw = String(input.siteName ?? '').trim();
  const site = numericTokens(siteRaw).length ? nameWithoutNumbers(siteRaw) || siteRaw : siteRaw;
  const place = String(input.regionName ?? '').trim();
  const band = copy.band.replace(/\s*·\s*무료\s*$/, '').trim();
  const d = ddayText(input.dday);
  const eyebrow = kind === 'sale' ? (d ? `분양 알림 · 청약 ${d}` : '분양 알림') : kind === 'home' ? '분양 알림' : band;
  // #4b(CL12) — 허브 폼 제목·첫 문장은 rank_lock 보존 문장(「{지역} 분양·미분양 안내 신청 이 지역에 새로 나오는 …」)이다 → 허브는 종전 copy.cta·copy.lede 그대로.
  const isHub = kind === 'redev_hub' || kind === 'area_hub';
  const titleName = kind === 'home' ? '관심 현장' : isHub ? copy.cta : (site || copy.cta);
  const titleSub = kind === 'resale' ? '매물·시세를 정리해 드려요' : isHub ? copy.lede : '소식을 가장 먼저 받아보세요';
  const note = ({
    sale: '입력 정보는 이 현장 분양 소식 안내에만 사용합니다',
    redev: '입력 정보는 이 구역 진행 소식 안내에만 사용합니다',
    resale: '입력 정보는 이 단지 매물·시세 안내에만 사용합니다',
    existing: '입력 정보는 같은 지역 분양 소식 안내에만 사용합니다',
    redev_hub: `입력 정보는 ${place ? `${place} ` : ''}구역 진행 소식 안내에만 사용합니다`,
    area_hub: `입력 정보는 ${place ? `${place} ` : ''}분양 소식 안내에만 사용합니다`,
    home: '입력 정보는 분양 소식 안내에만 사용합니다',
  } as const)[kind];
  const doneLine = (m: string) => ({
    sale: `분양가·일정이 확정되면 ${m} 로 알려드릴게요`,
    redev: `다음 단계 소식이 나오면 ${m} 로 알려드릴게요`,
    resale: `담당자가 ${m} 로 연락드릴게요`,
    existing: `새 분양 소식이 나오면 ${m} 로 알려드릴게요`,
    redev_hub: `구역 소식이 나오면 ${m} 로 알려드릴게요`,
    area_hub: `새 분양 소식이 나오면 ${m} 로 알려드릴게요`,
    home: `새 분양 소식이 나오면 ${m} 로 알려드릴게요`,
  } as const)[kind];
  return { eyebrow, titleName, titleSub, note, note2: NOTE2, sheetTitle: `${isHub ? (place || copy.cta) : titleName} 알림 받기`, doneLine };
}

/** 휴대폰 번호 → 「앞 3자리-****-뒤 4자리」 (뒤 4자리만 보인다). 자릿수가 모자라면 끝 4자리만 살린다. */
export function maskPhone(phone: string): string {
  const d = String(phone ?? '').replace(/[^0-9]/g, '');
  if (d.length < 4) return '***';
  const head = d.length >= 10 ? d.slice(0, 3) : '';
  return head ? `${head}-****-${d.slice(-4)}` : `****-${d.slice(-4)}`;
}

/* ───────── 일정 요약 3칸 ───────── */

export interface LeadSummaryCell { key: 'price' | 'special' | 'winner'; label: string; value: string }
export interface LeadSummary {
  /** 청약 접수 시작까지 남은 날(0 = 접수 중 또는 당일). 없으면 null. */
  dday: number | null;
  /** 2칸 이상일 때만 채운다(1칸 이하면 빈 배열 — 줄 전체 생략). */
  cells: LeadSummaryCell[];
}

const WD = ['일', '월', '화', '수', '목', '금', '토'];
/** YYYY-MM-DD → 「10.20(화)」 */
export function shortDay(iso: string): string {
  const [y, m, d] = iso.split('-').map(Number);
  const wd = WD[new Date(Date.UTC(y, m - 1, d)).getUTCDay()];
  return `${m}.${d}(${wd})`;
}

/**
 * 요약 3칸 재료. 서버 페이지가 부른다(사전에 가린 분양가만 price 로 넘긴다 — 결정 8·출처 없는 분양가는 이미 비어 있다).
 *  · 분양가: 확정 금액(price) → 그 값 / 공고 전(sale 계열) → 「확정 즉시」 / 그 밖 → 칸 없음.
 *  · 특별공급 접수일 · 당첨 발표일: 공고 날짜가 있을 때만.
 *  · 2칸 미만이면 cells = [] (분양예정·정비는 고정 문구 대신 아예 생략).
 */
export function leadSummaryOf(input: {
  sub: { announcement_date?: string | null; spsply_rcept_bgnde?: string | null; spsply_rcept_endde?: string | null; rcept_bgnde?: string | null; rcept_endde?: string | null; przwner_presnatn_de?: string | null } | null | undefined;
  today: string;
  /** 확정 분양가 표기(이미 가림 처리된 값). 없으면 null. */
  price?: string | null;
  /** 분양 계열(sale)인가 — 「확정 즉시」는 이 계열의 공고 전에만. */
  sale: boolean;
}): LeadSummary {
  const sub = input.sub ?? null;
  const ann = normDate(sub?.announcement_date ?? null);
  const spB = normDate(sub?.spsply_rcept_bgnde ?? null);
  const spE = normDate(sub?.spsply_rcept_endde ?? null);
  const rB = normDate(sub?.rcept_bgnde ?? null);
  const rE = normDate(sub?.rcept_endde ?? null);
  const win = normDate(sub?.przwner_presnatn_de ?? null);
  const cells: LeadSummaryCell[] = [];
  const price = String(input.price ?? '').trim();
  if (price) cells.push({ key: 'price', label: '분양가', value: price });
  else if (input.sale && !ann && !spB && !rB) cells.push({ key: 'price', label: '분양가', value: '확정 즉시' });
  if (spB) cells.push({ key: 'special', label: '특별공급 접수일', value: shortDay(spB) });
  if (win) cells.push({ key: 'winner', label: '당첨 발표일', value: shortDay(win) });

  let dday: number | null = null;
  if (input.sale) {
    for (const [b, e] of [[spB, spE], [rB, rE]] as const) {
      if (!b) continue;
      const toStart = daysBetween(input.today, b);
      const toEnd = daysBetween(input.today, e ?? b);
      const n = toStart >= 0 ? toStart : toEnd >= 0 ? 0 : null;
      if (n != null && (dday == null || n < dday)) dday = n;
    }
  }
  return { dday, cells: cells.length >= 2 ? cells : [] };
}

/* ───────── 전송 묶음 ───────── */

export interface LeadPayload {
  name: string;
  phone: string;
  birthDate: string;
  desiredType: string;
  consent: boolean;
  marketingOk: boolean;
  dwellMs: number;
  siteSlug: string;
  siteName: string;
  region: string;
  sigungu: string;
  interestRegion: string;
  siteUrl: string;
  inquiryType: string;
  entryPath: string;
  sourceDomain: string;
  channel?: string;
  company: string;
  query: Record<string, string>;
  leadRef: string;
}

/**
 * retention-1031 — 생년월일은 «선택» 이다(시작 161 → 제출 49, 70% 이탈 · 상세 폼 3칸 중 유일한 주민 정보).
 *   하류 확인: Apps Script(시트 leads 12열)·leads.birth_date 는 빈 값을 받는다 — 허브 폼이 9/23 부터 빈 값을 보내 왔고
 *   최근 60일 leads 6건이 birth_date NULL 로 정상 적재·시트 기록됐다. 노본 영업 시트는 지역·현장으로 나누며 생년월일을 쓰지 않는다.
 *   ⚠️ Node 9/26 「생년월일 필수 유지」 를 뒤집는 값이다 — 되돌리려면 이 상수 하나만 true 로.
 */
export const LEAD_BIRTH_REQUIRED = false;

/** 순수 — 생년월일 칸 오류 문구(없으면 null). 필수가 아니면 «비었거나 6자리» 면 통과. */
export function leadBirthError(raw: string, required: boolean = LEAD_BIRTH_REQUIRED): string | null {
  const d = String(raw ?? '').replace(/\D/g, '');
  if (required) return d.length === 6 ? null : '생년월일 6자리를 입력해 주세요';
  return d.length === 0 || d.length === 6 ? null : '생년월일은 6자리로 입력하거나 비워 두세요';
}

/**
 * payload 조립 — 종전 LeadForm.handleSubmit 안의 객체 리터럴을 «바이트 의미 그대로» 옮겼다.
 * 바뀐 것은 desiredType 하나(화면 칸 제거 → 「미정」 고정). 키 집합·순서·channel(홈만)·허니팟(company)·query 는 그대로다.
 */
export function buildLeadPayload(input: {
  name: string; phone: string; birthDate: string; consent: boolean; marketingOk: boolean; dwellMs: number;
  siteSlug: string; siteName: string; regionText: string; sigunguText: string; siteUrl: string; inquiryType: string;
  entryPath: string; sourceDomain: string; isHome: boolean; company: string; query: Record<string, string>; leadRef: string;
}): LeadPayload {
  return {
    name: input.name,
    phone: input.phone, // 원본 그대로. 정규화는 서버가 한다
    birthDate: input.birthDate,
    desiredType: LEAD_DESIRED_TYPE_FIXED,
    consent: input.consent,
    marketingOk: input.marketingOk,
    dwellMs: input.dwellMs, // 3초 미만 판정은 서버가 한다 — 여기서 막지 않는다
    siteSlug: input.siteSlug,
    siteName: input.siteName,
    region: input.regionText,
    sigungu: input.sigunguText,
    interestRegion: [input.regionText, input.sigunguText].filter(Boolean).join(' '),
    siteUrl: input.siteUrl,
    inquiryType: input.inquiryType,
    entryPath: input.entryPath,
    sourceDomain: input.sourceDomain,
    ...(input.isHome ? { channel: 'home' } : {}),
    company: input.company,
    query: input.query,
    leadRef: input.leadRef,
  };
}
